
import { supabase } from "@/integrations/supabase/client";
import { Movie } from "@/types/movie";
import { MovieNotFoundError, isKnownFailedMovie, addFailedMovieId } from "@/utils/movieRequestCache";
import { normalizeImagePaths } from "@/utils/imageUtils";
import { fromDbRow } from "@/utils/fromDbRow";

// Interface for the movie details response from TMDb API - Pure camelCase
export interface MovieDetails {
  tmdbId: number;
  title: string;
  director: string;
  year?: number;
  releaseDate?: string;
  runtime?: number;
  genres?: { id: number; name: string }[];
  posterPath?: string | null; // Pure camelCase
  backdropPath?: string | null; // Pure camelCase
  overview?: string | null;
  voteAverage?: number;
  popularity?: number;
}

/**
 * Creates a promise that will reject after the specified timeout
 */
function createTimeout(ms: number): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`Request timed out after ${ms}ms`)), ms);
  });
}

/**
 * Normalizes MovieDetails from TMDb API format to our app's Movie format
 * Pure camelCase internally
 */
export function normalizeMovieDetails(details: MovieDetails): Movie {
  const genreNames = details.genres?.map(genre => genre.name) || [];
  
  const normalized = {
    tmdbId: details.tmdbId,
    title: details.title,
    director: details.director,
    year: details.year || (details.releaseDate ? new Date(details.releaseDate).getFullYear() : 0),
    tags: genreNames,
    posterPath: details.posterPath, // Pure camelCase
    backdropPath: details.backdropPath, // Pure camelCase
    overview: details.overview,
    favorite: false,
    planToWatch: false, // Pure camelCase
    genres: genreNames,
    releaseDate: details.releaseDate, // Pure camelCase
    runtime: details.runtime,
    voteAverage: details.voteAverage, // Pure camelCase
    popularity: details.popularity,
    source: "movie" as const
  };
  
  return normalizeImagePaths(normalized);
}

/**
 * Fetches detailed movie information by TMDb ID with aggressive timeout
 */
export async function fetchMovieDetails(
  tmdbId: number, 
  signal?: AbortSignal,
  uid?: string
): Promise<Movie> {
  console.log(`[fetchMovieDetails] Fetching tmdbId: ${tmdbId}, uid: ${uid || 'not provided'}`);
  
  // Check if this ID is known to fail
  if (isKnownFailedMovie(tmdbId)) {
    console.warn(`Skipping known failed movie ID: ${tmdbId}`);
    throw new MovieNotFoundError(tmdbId);
  }
  
  const options: any = {
    body: { 
      tmdbId: tmdbId, 
      uid 
    }
  };
  
  if (signal) {
    options.signal = signal;
  }
  
  try {
    // More aggressive timeout for mobile - 8 seconds
    const TIMEOUT_MS = 8000;
    
    const result = await Promise.race([
      supabase.functions.invoke('get_movie_details', options),
      createTimeout(TIMEOUT_MS)
    ]);
    
    const { data, error } = result as { data: any, error: any };
    
    if (error) {
      console.error('[fetchMovieDetails] Error:', error);
      
      if (error.message && (error.message.includes('404') || error.status === 404)) {
        addFailedMovieId(tmdbId);
        throw new MovieNotFoundError(tmdbId);
      }
      
      throw error;
    }
    
    if (data && data.error) {
      console.error('[fetchMovieDetails] Edge function error:', data.error);
      
      if (data.status === 404 || data.error.includes('404')) {
        addFailedMovieId(tmdbId);
        throw new MovieNotFoundError(tmdbId);
      }
      
      throw new Error(data.message || data.error);
    }
    
    console.log('[fetchMovieDetails] Success:', data);
    return normalizeMovieDetails(data as MovieDetails);
  } catch (error: any) {
    // Handle timeout errors specifically
    if (error.message?.includes('timed out')) {
      console.error(`[fetchMovieDetails] Timeout for movie ID ${tmdbId}`);
      throw new Error(`Request timed out. Please try again.`);
    }
    
    // Check if response includes a status field indicating error
    if (error.status === 404 || (error.data && error.data.status === 404)) {
      addFailedMovieId(tmdbId);
      throw new MovieNotFoundError(tmdbId);
    }
    
    throw error;
  }
}
